import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Users, Target, Heart, Award, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { impactStats, testimonials } from '../mockData';
import { projectDetails } from '../data/projectDetails';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await axios.get(`${API}/projects`);
        setProjects(response.data);
      } catch (error) {
        console.error('Error fetching projects:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  const iconMap = {
    Users: Users,
    Target: Target,
    Heart: Heart,
    Award: Award
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%2353a5ca" fill-opacity="0.4"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'
          }} />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block px-4 py-2 rounded-full mb-6" style={{ backgroundColor: '#53a5ca20' }}>
              <span className="text-sm font-semibold !text-[#53A5CA]" style={{ color: '#53a5ca' }}>NAIWA Daughters Cherokee Collective</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight" style={{ color: '#7e2f22' }}>
              Empowering Young Indigenous Women Leaders
            </h1>
            <p className="text-lg md:text-xl text-gray-600 mb-8 leading-relaxed max-w-3xl mx-auto">
              Creating a safe haven for Indigenous young women through leadership development, cultural enrichment, and community service.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to="/join">
                <Button
                  size="lg"
                  className="text-white hover:opacity-90 transition-all duration-200 text-base px-8"
                  style={{ backgroundColor: '#53a5ca' }}>

                  Join NAIWA Daughters
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/support">
                <Button
                  size="lg"
                  variant="outline"
                  className="text-base px-8 border-2 transition-all duration-200"
                  style={{ borderColor: '#e37c55', color: '#e37c55' }}>

                  Support Our Mission
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Stats */}
      <section className="py-16" style={{ backgroundColor: '#53a5ca' }}>
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {impactStats.map((stat, index) => {
              const IconComponent = iconMap[stat.icon];
              return (
                <div key={index} className="text-center">
                  <div className="flex justify-center mb-3">
                    <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                      <IconComponent className="h-6 w-6 text-white" />
                    </div>
                  </div>
                  <div className="text-3xl md:text-4xl font-bold text-white mb-2">{stat.number}</div>
                  <div className="text-sm md:text-base text-white/90">{stat.label}</div>
                </div>);

            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-8 items-start">
            <div className="space-y-5">
              <div className="inline-block px-3 py-1 rounded-full text-xs font-semibold" style={{ backgroundColor: '#e37c5520', color: '#e37c55' }}>
                Recent success spotlight
              </div>
              <h2 className="text-3xl md:text-4xl font-bold leading-tight" style={{ color: '#7e2f22' }}>Celebrating momentum, visibility, and community wins.</h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                A special spotlight of one of our NAIWA Daughters’ most recent successes please show the some love and like/share this.
              </p>
              <Link to="/impact">
                <Button className="text-white" style={{ backgroundColor: '#53a5ca' }} data-testid="home-spotlight-impact-link">
                  View Impact &amp; Outcomes
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>

            <div className="rounded-[28px] overflow-hidden shadow-xl bg-white p-2" data-testid="home-success-spotlight-embed">
              <iframe
                src="https://www.facebook.com/plugins/post.php?href=https%3A%2F%2Fwww.facebook.com%2Fnaiwadaughterscherokee%2Fposts%2Fpfbid02JrjDBy6yzSgVDZ6ESBCvhMQJkBhaXbjXxB3XfQ2hWXZTcnyDqaEs5VNkATkQdJbNl&show_text=true&width=500"
                width="100%"
                height="540"
                style={{ border: 'none', overflow: 'hidden' }}
                scrolling="no"
                frameBorder="0"
                allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                allowFullScreen
                title="NAIWA Daughters recent success spotlight"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
            <div className="space-y-4">
              <div className="inline-block px-3 py-1 rounded-full text-xs font-semibold" style={{ backgroundColor: '#dbbf5720', color: '#dbbf57' }}>
                OUR MISSION
              </div>
              <h2 className="text-3xl md:text-4xl font-bold leading-tight" style={{ color: '#7e2f22' }}>
                Building Tomorrow's Leaders Today
              </h2>
              <p className="text-gray-600 leading-relaxed text-lg">
                Empowering young Indigenous women to become compassionate leaders who uplift their communities and preserve their heritage through leadership development, cultural enrichment, and community service.
              </p>
            </div>
            <div className="space-y-4">
              <div className="inline-block px-3 py-1 rounded-full text-xs font-semibold" style={{ backgroundColor: '#e37c5520', color: '#e37c55' }}>
                OUR VISION
              </div>
              <h2 className="text-3xl md:text-4xl font-bold leading-tight" style={{ color: '#7e2f22' }}>
                A Connected Network Across Turtle Island
              </h2>
              <p className="text-gray-600 leading-relaxed text-lg">We envision a strong, interconnected network of NAIWA Daughters chapters across Turtle Island, cultivating young Indigenous women leaders who create lasting impact for generations to come.

              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Projects */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: '#7e2f22' }}>Our Priority Projects</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Initiatives that empower, educate, and create lasting change in Indigenous communities
            </p>
          </div>
          {loading ?
          <div className="text-center py-12">
              <p className="text-gray-500">Loading projects...</p>
            </div> :

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
              {projects.map((project) =>
            <Card key={project.id} className="hover:shadow-lg transition-all duration-300 border-0 overflow-hidden group">
                <Link to={`/projects/${project.slug}`} data-testid={`home-project-image-link-${project.slug}`}>
                  <div className="relative h-48 overflow-hidden cursor-pointer">
                    <img
                    src={projectDetails[project.slug]?.heroImage || project.image}
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />

                    <div className="absolute top-3 right-3">
                      <span className="px-3 py-1 rounded-full text-xs font-semibold text-white" style={{ backgroundColor: project.status === 'Active' ? '#53a5ca' : '#dbbf57' }}>
                        {project.status}
                      </span>
                    </div>
                  </div>
                </Link>
                <CardHeader>
                  <CardTitle className="text-xl" style={{ color: '#7e2f22' }}>{project.title}</CardTitle>
                  <CardDescription className="text-sm">{project.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold" style={{ color: '#53a5ca' }}>{project.impact}</span>
                    <Link to={`/projects/${project.slug}`}>
                      <Button variant="ghost" size="sm" className="group-hover:translate-x-1 transition-transform" data-testid={`home-project-learn-more-${project.slug}`}>
                        Learn More
                        <ArrowRight className="ml-1 h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            )}\n          </div>
          }
          <div className="text-center mt-10">
            <Link to="/projects">
              <Button
                variant="outline"
                size="lg"
                className="border-2"
                style={{ borderColor: '#53a5ca', color: '#53a5ca' }}>

                View All Projects
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: '#7e2f22' }}>Voices from Our Community</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">Hear from our members and mentors about the impact of NAIWA Daughters

            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {testimonials.map((testimonial) =>
            <Card key={testimonial.id} className="border-0 shadow-md">
                <CardContent className="pt-6">
                  <div className="mb-4">
                    <svg className="w-10 h-10 opacity-20" style={{ color: '#53a5ca' }} fill="currentColor" viewBox="0 0 24 24">
                      <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                    </svg>
                  </div>
                  <p className="text-gray-700 mb-6 leading-relaxed italic">"{testimonial.quote}"</p>
                  <div className="border-t pt-4">
                    <p className="font-semibold" style={{ color: '#7e2f22' }}>{testimonial.author}</p>
                    <p className="text-sm text-gray-500">{testimonial.role}</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 relative overflow-hidden" style={{ backgroundColor: '#7e2f22' }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width="80" height="80" viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.4"%3E%3Cpath d="M50 50c0-5.523 4.477-10 10-10s10 4.477 10 10-4.477 10-10 10c0 5.523-4.477 10-10 10s-10-4.477-10-10 4.477-10 10-10zM10 10c0-5.523 4.477-10 10-10s10 4.477 10 10-4.477 10-10 10c0 5.523-4.477 10-10 10S0 25.523 0 20s4.477-10 10-10zm10 8c4.418 0 8-3.582 8-8s-3.582-8-8-8-8 3.582-8 8 3.582 8 8 8zm40 40c4.418 0 8-3.582 8-8s-3.582-8-8-8-8 3.582-8 8 3.582 8 8 8z" /%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'
          }} />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
              Join the Movement
            </h2>
            <p className="text-lg md:text-xl text-white/90 mb-10 leading-relaxed">
              Whether you're an Indigenous young woman ready to lead, a mentor wanting to give back, or a supporter who believes in our mission—there's a place for you here.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/join">
                <Button
                  size="lg"
                  className="text-white hover:opacity-90 transition-all duration-200 text-base px-8"
                  style={{ backgroundColor: '#53a5ca' }}>

                  Become a Member
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/mentor">
                <Button
                  size="lg"
                  className="bg-white hover:bg-gray-100 transition-all duration-200 text-base px-8"
                  style={{ color: '#7e2f22' }}>

                  Become a Mentor
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/support">
                <Button
                  size="lg"
                  className="text-white hover:opacity-90 transition-all duration-200 text-base px-8"
                  style={{ backgroundColor: '#e37c55' }}>

                  Support Our Work
                  <Heart className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Learn More */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#7e2f22' }}>Featured In</h3>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <a
                href="https://theonefeather.com/2024/02/20/a-safe-haven-junior-naiwa-cherokee-group-breaking-new-ground/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between p-6 bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-200 group">

                <div>
                  <p className="font-semibold mb-1" style={{ color: '#7e2f22' }}>The Cherokee One Feather</p>
                  <p className="text-sm text-gray-600">"A safe haven: Junior NAIWA Cherokee group breaking new ground"</p>
                </div>
                <ExternalLink className="h-5 w-5 text-gray-400 group-hover:text-gray-600 transition-colors" />
              </a>
              <a
                href="https://www.talielohi.com/community-support/naiwadaughters"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between p-6 bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-200 group">

                <div>
                  <p className="font-semibold mb-1" style={{ color: '#7e2f22' }}>Tali Elohi</p>
                  <p className="text-sm text-gray-600">Community Support Partnership</p>
                </div>
                <ExternalLink className="h-5 w-5 text-gray-400 group-hover:text-gray-600 transition-colors" />
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>);

};

export default Home;